// The few marks an answer may carry (the brief names them: **bold**, *italics*
// and "- " list lines), parsed into runs the chat renders as styled text. Not a
// Markdown renderer: anything else — headings, links, tables — stays text, and
// an unmatched star stays a star. The app's copy is mobile/lib/chat/answer_text.dart.

export type Run = { text: string; bold?: boolean; italic?: boolean };

const mark = /\*\*(\S(?:.*?\S)?)\*\*|\*(\S(?:.*?\S)?)\*/g;
const bullet = /^(\s*)[-*]\s+/;

function lineRuns(line: string, runs: Run[]) {
  let at = 0;
  for (const m of line.matchAll(mark)) {
    if (m.index > at) runs.push({ text: line.slice(at, m.index) });
    runs.push(m[1] !== undefined ? { text: m[1], bold: true } : { text: m[2] ?? "", italic: true });
    at = m.index + m[0].length;
  }
  return at;
}

export function answerRuns(answer: string): Run[] {
  const runs: Run[] = [];
  const lines = answer.split("\n");
  lines.forEach((raw, i) => {
    const line = raw.replace(bullet, "$1• ");
    const at = lineRuns(line, runs);
    const tail = line.slice(at) + (i < lines.length - 1 ? "\n" : "");
    if (tail) runs.push({ text: tail });
  });
  return runs;
}

/**
 * One word or phrase of a Meaning answer (ADR-0216): its plain sense, absent
 * for a pointing word, and what it means here. Both keep their marks, for
 * answerRuns.
 */
export type MeaningEntry = { heading: string; plain?: string; here: string };

const headingLine = /^\*\*(\S(?:.*?\S)?)\*\*\s*:?$/;
const hereLine = /^[—–]\s*(\S.*)$/;
const quotes = /^["“«‘']+|["”»’':]+$/g;
// A list mark the model put before the plain sense (seen live on Hamlet), which
// the card would otherwise show: "- Pray now".
const listMark = /^[-*•]\s+/;

/**
 * The answer as entries when every block of it is one, or null and it is
 * shown as prose (ADR-0216: all or nothing, so an old answer or a model slip
 * loses nothing). The format is testdata/chat/meaning.json, shared with the
 * app's copy and pinned on the prompt's side by the Meaning clauses.
 */
export function meaningEntries(answer: string): MeaningEntry[] | null {
  const blocks = answer.trim().split(/\n\s*\n/);
  const entries: MeaningEntry[] = [];
  for (const block of blocks) {
    const lines = block.split("\n").map((l) => l.trim());
    const [first = ""] = lines;
    const plain = (lines[1] ?? "").replace(listMark, "");
    const heading = headingLine.exec(first)?.[1]?.replace(quotes, "").trim();
    const here = hereLine.exec(lines[lines.length - 1] ?? "")?.[1];
    if (!heading || !here) return null;
    if (lines.length === 2) entries.push({ heading, here });
    else if (lines.length === 3 && !hereLine.test(plain) && !headingLine.test(plain))
      entries.push({ heading, plain, here });
    else return null;
  }
  return entries.length ? entries : null;
}

/** A part of an entry as runs: its marks, never a list bullet. */
export function partRuns(text: string): Run[] {
  const runs: Run[] = [];
  const at = lineRuns(text, runs);
  if (at < text.length) runs.push({ text: text.slice(at) });
  return runs;
}
